import React from "react";
import { Tooltip } from "@material-ui/core";

const OwnershipTokens = ({
  ownershipByPlayer = {},
  playerPortfolios = {},
  itemName = "",
  playerColors = {},
  playerEmojis = {},
  roundNumber = 1,
  getPurchasePrice = null,
  // Forced sale props
  actionMode = null,
  currentPlayerName = null,
  onForcedSaleClick = null
}) => {
  if (!ownershipByPlayer || Object.keys(ownershipByPlayer).length === 0) {
    return null;
  }

  // Group holdings by purchase round (previous rounds vs current round)
  // Also separate ownership tokens from sell tokens
  const previousRoundsHoldings = [];
  const currentRoundHoldings = [];
  const sellTokens = [];

  Object.entries(ownershipByPlayer).forEach(([playerName, holdings]) => {
    const playerColor = playerColors[playerName] || '#666666';
    const playerEmoji = playerEmojis[playerName] || '🎮';
    
    // Holdings is now an array of objects with purchaseRound, purchasePrice, saleRound, salePrice, and type
    const holdingsArray = Array.isArray(holdings) ? holdings : [];
    
    holdingsArray.forEach((holding, index) => {
      // Check if this is a sell token
      if (holding.type === 'sell') {
        const saleRound = holding.saleRound || 0;
        const salePrice = holding.salePrice || null;
        
        const tokenData = {
          playerName,
          playerColor,
          playerEmoji,
          saleRound,
          salePrice,
          key: `sell-${playerName}-${saleRound}-${index}`
        };
        
        sellTokens.push(tokenData);
        return;
      }
      
      // This is an ownership token
      const purchaseRound = holding.purchaseRound || 0;
      const purchasePrice = holding.purchasePrice || null;
      const isCurrentRound = purchaseRound === roundNumber;
      
      const tokenData = {
        playerName,
        playerColor,
        playerEmoji,
        purchaseRound,
        purchasePrice,
        key: `${playerName}-${purchaseRound}-${index}`
      };
      
      if (isCurrentRound) {
        currentRoundHoldings.push(tokenData);
      } else {
        previousRoundsHoldings.push(tokenData);
      }
    });
  });

  const renderToken = (tokenData) => {
    const purchasePrice = tokenData.purchasePrice;
    const isOtherPlayer = tokenData.playerName !== currentPlayerName;
    const isForcedSaleMode = actionMode === "selecting_forced_sale";
    const isClickable = isForcedSaleMode && isOtherPlayer && onForcedSaleClick;
    
    let tooltipTitle = purchasePrice !== null && purchasePrice !== undefined
      ? `Purchased in Round ${tokenData.purchaseRound} for $${purchasePrice}`
      : `Purchased in Round ${tokenData.purchaseRound}`;
    
    if (isClickable) {
      tooltipTitle = `Click to force ${tokenData.playerName} to sell this stock`;
    }
    
    const handleClick = () => {
      if (isClickable) {
        onForcedSaleClick(tokenData.playerName, itemName);
      }
    };
    
    return (
      <Tooltip title={tooltipTitle} arrow>
        <div
          key={tokenData.key}
          className={`ownership-token ${isClickable ? 'ownership-token-clickable' : ''}`}
          style={{
            backgroundColor: tokenData.playerColor,
            borderColor: tokenData.playerColor,
            boxShadow: `0 2px 6px ${tokenData.playerColor}80, inset 0 1px 2px rgba(255,255,255,0.2)`,
            cursor: isClickable ? 'pointer' : 'default',
            animation: isClickable ? 'pulse-forced-sale 1.5s ease-in-out infinite' : 'none'
          }}
          onClick={handleClick}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = isClickable ? 'scale(1.2)' : 'scale(1.1)';
            e.currentTarget.style.boxShadow = isClickable 
              ? `0 4px 14px ${tokenData.playerColor}C0, inset 0 1px 2px rgba(255,255,200,0.5)`
              : `0 3px 10px ${tokenData.playerColor}A0, inset 0 1px 2px rgba(255,255,200,0.4)`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = `0 2px 6px ${tokenData.playerColor}80, inset 0 1px 2px rgba(255,255,200,0.4)`;
          }}
        >
          <span className="ownership-token-emoji">
            {tokenData.playerEmoji}
          </span>
          <span className="ownership-token-count">
            x1
          </span>
        </div>
      </Tooltip>
    );
  };

  const renderSellToken = (tokenData) => {
    const salePrice = tokenData.salePrice;
    const tooltipTitle = salePrice !== null && salePrice !== undefined
      ? `Sold in Round ${tokenData.saleRound} for $${salePrice}`
      : `Sold in Round ${tokenData.saleRound}`;
    
    return (
      <Tooltip title={tooltipTitle} arrow>
        <div
          key={tokenData.key}
          className="ownership-token sell-token"
          style={{
            backgroundColor: tokenData.playerColor,
            borderColor: tokenData.playerColor,
            boxShadow: `0 2px 6px ${tokenData.playerColor}80, inset 0 1px 2px rgba(255,255,255,0.2)`,
            opacity: 0.7,
            borderStyle: 'dashed'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = `0 3px 10px ${tokenData.playerColor}A0, inset 0 1px 2px rgba(255,255,200,0.4)`;
            e.currentTarget.style.opacity = '0.9';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = `0 2px 6px ${tokenData.playerColor}80, inset 0 1px 2px rgba(255,255,200,0.4)`;
            e.currentTarget.style.opacity = '0.7';
          }}
        >
          <span className="ownership-token-emoji">
            {tokenData.playerEmoji}
          </span>
          <span className="ownership-token-count sell-token-symbol">
            −
          </span>
        </div>
      </Tooltip>
    );
  };

  // Count tokens per player for previous rounds (to show combined count)
  // Also track purchase rounds and prices for tooltip
  const previousRoundsCounts = {};
  previousRoundsHoldings.forEach(token => {
    if (!previousRoundsCounts[token.playerName]) {
      previousRoundsCounts[token.playerName] = {
        count: 0,
        playerColor: token.playerColor,
        playerEmoji: token.playerEmoji,
        purchaseRounds: [],
        purchasePrices: []
      };
    }
    previousRoundsCounts[token.playerName].count++;
    if (token.purchaseRound) {
      previousRoundsCounts[token.playerName].purchaseRounds.push(token.purchaseRound);
    }
    if (token.purchasePrice !== null && token.purchasePrice !== undefined) {
      previousRoundsCounts[token.playerName].purchasePrices.push(token.purchasePrice);
    }
  });

  const hasPreviousRounds = Object.keys(previousRoundsCounts).length > 0;
  const hasCurrentRound = currentRoundHoldings.length > 0;
  const hasSellTokens = sellTokens.length > 0;

  if (!hasPreviousRounds && !hasCurrentRound && !hasSellTokens) {
    return null;
  }

  return (
    <div className="ownership-tokens-container">
      {/* Sell tokens row - on top (most recent activity) */}
      {hasSellTokens && (
        <div className="ownership-tokens-row ownership-tokens-sell">
          {sellTokens.map(tokenData => renderSellToken(tokenData))}
        </div>
      )}
      
      {/* Current round row - ownership tokens purchased this round */}
      {hasCurrentRound && (
        <div className="ownership-tokens-row ownership-tokens-current">
          {currentRoundHoldings.map(tokenData => renderToken(tokenData))}
        </div>
      )}
      
      {/* Divider line - always show above previous round tokens if they exist */}
      {hasPreviousRounds && (
        <div className="ownership-tokens-divider"></div>
      )}
      
      {/* Previous rounds row - on bottom */}
      {hasPreviousRounds && (
        <div className="ownership-tokens-row ownership-tokens-previous">
          {Object.entries(previousRoundsCounts).map(([playerName, data]) => {
            const isOtherPlayer = playerName !== currentPlayerName;
            const isForcedSaleMode = actionMode === "selecting_forced_sale";
            const isClickable = isForcedSaleMode && isOtherPlayer && onForcedSaleClick;
            
            // Create tooltip text showing all purchase rounds and prices
            const roundsText = data.purchaseRounds.length > 0 
              ? `Rounds: ${[...new Set(data.purchaseRounds)].sort((a, b) => a - b).join(', ')}`
              : '';
            const pricesText = data.purchasePrices.length > 0
              ? `Prices: $${[...new Set(data.purchasePrices)].sort((a, b) => a - b).join(', $')}`
              : '';
            let tooltipTitle = [roundsText, pricesText].filter(Boolean).join(' | ') || `Purchased in previous rounds`;
            
            if (isClickable) {
              tooltipTitle = `Click to force ${playerName} to sell one share`;
            }
            
            const handleClick = () => {
              if (isClickable) {
                onForcedSaleClick(playerName, itemName);
              }
            };
            
            return (
              <Tooltip key={`prev-${playerName}`} title={tooltipTitle} arrow>
                <div
                  className={`ownership-token ${isClickable ? 'ownership-token-clickable' : ''}`}
                  style={{
                    backgroundColor: data.playerColor,
                    borderColor: data.playerColor,
                    boxShadow: `0 2px 6px ${data.playerColor}80, inset 0 1px 2px rgba(255,255,255,0.2)`,
                    cursor: isClickable ? 'pointer' : 'default',
                    animation: isClickable ? 'pulse-forced-sale 1.5s ease-in-out infinite' : 'none'
                  }}
                  onClick={handleClick}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = isClickable ? 'scale(1.2)' : 'scale(1.1)';
                    e.currentTarget.style.boxShadow = isClickable 
                      ? `0 4px 14px ${data.playerColor}C0, inset 0 1px 2px rgba(255,255,200,0.5)`
                      : `0 3px 10px ${data.playerColor}A0, inset 0 1px 2px rgba(255,255,200,0.4)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = `0 2px 6px ${data.playerColor}80, inset 0 1px 2px rgba(255,255,200,0.4)`;
                  }}
                >
                  <span className="ownership-token-emoji">
                    {data.playerEmoji}
                  </span>
                  <span className="ownership-token-count">
                    x{data.count}
                  </span>
                </div>
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OwnershipTokens;

